import React, { useEffect, useState, useCallback } from 'react';
import { Plus, X, Check, Pencil, Trash2, AlertCircle } from 'lucide-react';
import api from '../../api/client';

// Piani rate per pacchetto (16/10/2026, decisioni titolare): per ogni ciclo e pacchetto le rate con la
// scadenza; un piano vale per tutte le sedi oppure solo per una (vince quello della sede). La quota
// tesseramento si aggiunge da sola alla 1ª rata; con un prezzo personalizzato le rate si riducono in
// proporzione. Nessuna tolleranza: una rata non coperta è in ritardo dal giorno dopo la scadenza.
const eur = (n) => `${(+n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const gg = (d) => d ? d.split('-').reverse().join('/') : '—';
const cent = (n) => Math.round(n * 100) / 100;

export default function PianiRate({ cicloId, ciclo, puoModificare }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState('');
  const [tutti, setTutti] = useState(false);
  const [editor, setEditor] = useState(null); // { pacchetto_id, sede_nome, rate:[{importo,scadenza}] }
  const [salvando, setSalvando] = useState(false);

  const carica = useCallback(() => {
    if (!cicloId) return;
    setErrore('');
    api.get('/planning/piani-rate', { params: { ciclo_id: cicloId } })
      .then(r => setDati(r.data))
      .catch(e => setErrore(e.response?.data?.error || 'Errore nel caricamento dei piani'));
  }, [cicloId]);
  useEffect(() => { carica(); setEditor(null); }, [carica]);

  if (errore) return <div style={s.avviso}><AlertCircle size={14}/> {errore}</div>;
  if (!dati) return <div style={{ color:'var(--text-muted)', padding:'20px' }}>Caricamento…</div>;

  const sediCiclo = dati.sedi;
  const quota = ciclo?.quota_tesseramento;
  const pertinente = (p) => !p.sedi.length || p.sedi.some(n => sediCiclo.includes(n));
  const pianiDi = (id) => dati.piani.filter(x => x.pacchetto_id === id)
    .sort((a, b) => (a.sede_nome || '').localeCompare(b.sede_nome || ''));
  const pacchetti = dati.pacchetti.filter(p => tutti || pertinente(p) || pianiDi(p.id).length);

  const apri = (p, piano, sede_nome = null) => setEditor({
    pacchetto_id: p.id, prezzo: p.importo, nome: p.nome, sede_nome: piano ? piano.sede_nome : sede_nome,
    nuovo: !piano,
    rate: piano ? piano.rate.map(r => ({ importo: String(r.importo), scadenza: r.scadenza })) : [{ importo: String(p.importo), scadenza: '' }],
  });

  const salva = async (rate) => {
    setSalvando(true);
    try {
      await api.put('/planning/piani-rate', { ciclo_id: cicloId, pacchetto_id: editor.pacchetto_id, sede_nome: editor.sede_nome, rate });
      setEditor(null); carica();
    } catch (e) { alert(e.response?.data?.error || 'Errore nel salvataggio'); }
    finally { setSalvando(false); }
  };
  const elimina = () => {
    if (!window.confirm(`Togliere il piano rate di "${editor.nome}"${editor.sede_nome ? ` per ${editor.sede_nome}` : ''}? I corsisti di quel pacchetto non avranno più scadenze.`)) return;
    salva([]);
  };

  return (
    <div>
      <div style={s.info}>
        Per ogni pacchetto scrivi le rate e la data entro cui vanno pagate. La somma delle rate deve essere il prezzo del pacchetto.
        {quota > 0 && <> La <b>quota tesseramento ({eur(quota)})</b> si aggiunge da sola alla 1ª rata.</>}
        {' '}Se a una persona hai dato un prezzo diverso, le sue rate si riducono in proporzione e le date restano.
        Una rata non pagata è <b>in ritardo dal giorno dopo</b> la scadenza. Un piano può valere per tutte le sedi o solo per una (vince quello della sede).
      </div>
      <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'var(--font-size-xs)', margin:'0 0 12px', cursor:'pointer' }}>
        <input type="checkbox" checked={tutti} onChange={e => setTutti(e.target.checked)} style={{ accentColor:'#F4600C' }}/>
        Mostra anche i pacchetti non legati alle sedi di questo ciclo
      </label>

      {pacchetti.length === 0 && <div style={{ color:'var(--text-muted)', padding:'20px' }}>Nessun pacchetto corso attivo per le sedi di questo ciclo.</div>}

      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        {pacchetti.map(p => {
          const piani = pianiDi(p.id);
          const sediLibere = sediCiclo.filter(n => (!p.sedi.length || p.sedi.includes(n)) && !piani.some(x => x.sede_nome === n));
          const inEditor = editor && editor.pacchetto_id === p.id;
          return (
            <div key={p.id} style={s.card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'10px', flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontWeight:700 }}>{p.nome} <span style={{ color:'#C24B08' }}>{eur(p.importo)}</span></div>
                  <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>
                    {p.frequenza === 'BI' ? 'Bisettimanale' : 'Monosettimanale'} · {p.categoria === 'UNDER18' ? 'Under 18' : p.categoria === 'OPEN' ? 'Open' : 'Open e Under 18'}{p.annuale ? ' · annuale' : ''} · {p.sedi.length ? p.sedi.join(', ') : 'tutte le sedi'}
                  </div>
                </div>
                {puoModificare && !inEditor && (
                  <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                    {!piani.some(x => !x.sede_nome) && <button style={s.btnSec} onClick={() => apri(p, null)}><Plus size={12}/> Piano rate</button>}
                    {sediLibere.length > 0 && sediCiclo.length > 1 && (
                      <select style={{ ...s.inp, width:'auto', padding:'6px 10px', fontSize:'var(--font-size-xs)' }} value=""
                        onChange={e => e.target.value && apri(p, null, e.target.value)}>
                        <option value="">+ piano diverso per una sede…</option>
                        {sediLibere.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    )}
                  </div>
                )}
              </div>

              {piani.length === 0 && !inEditor && <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)', marginTop:'8px' }}>Nessun piano: per questo pacchetto non ci sono scadenze.</div>}

              {piani.map(piano => (
                (inEditor && !editor.nuovo && editor.sede_nome === piano.sede_nome) ? null : (
                  <div key={piano.sede_nome || ''} style={s.piano}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                      <span style={s.etichetta}>{piano.sede_nome ? `SOLO ${piano.sede_nome}` : (piani.length > 1 ? 'ALTRE SEDI' : 'TUTTE LE SEDI')}</span>
                      {puoModificare && !editor && <button style={s.icona} title="Modifica" onClick={() => apri(p, piano)}><Pencil size={12}/></button>}
                    </div>
                    <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                      {piano.rate.map(r => (
                        <span key={r.numero} style={s.rata}>
                          <b>{r.numero}ª</b> {eur(r.importo)}{r.numero === 1 && quota > 0 && <span style={{ color:'var(--text-muted)' }}> + {eur(quota)}</span>} · entro {gg(r.scadenza)}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              ))}

              {inEditor && <Editor editor={editor} setEditor={setEditor} quota={quota} salvando={salvando} onSalva={salva} onElimina={elimina}/>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Editor({ editor, setEditor, quota, salvando, onSalva, onElimina }) {
  const rate = editor.rate;
  const set = (k, campo, v) => setEditor(e => ({ ...e, rate: e.rate.map((r, i) => i === k ? { ...r, [campo]: v } : r) }));
  const somma = cent(rate.reduce((t, r) => t + (parseFloat(String(r.importo).replace(',', '.')) || 0), 0));
  const torna = Math.abs(somma - editor.prezzo) < 0.01;
  const dividi = (n) => {
    const base = Math.floor(editor.prezzo / n * 100) / 100;
    setEditor(e => ({ ...e, rate: Array.from({ length: n }, (_, i) => ({
      importo: String(i === n - 1 ? cent(editor.prezzo - base * (n - 1)) : base),
      scadenza: e.rate[i]?.scadenza || '',
    })) }));
  };
  const pronta = torna && rate.length > 0 && rate.every(r => r.scadenza && parseFloat(String(r.importo).replace(',', '.')) > 0);

  return (
    <div style={{ ...s.piano, border:'1px solid #F4600C55' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'8px' }}>
        <span style={s.etichetta}>{editor.sede_nome ? `PIANO SOLO PER ${editor.sede_nome}` : 'PIANO PER TUTTE LE SEDI'}</span>
        <span style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>
          Dividi in {[1,2,3,4].map(n => <button key={n} type="button" style={s.mini} onClick={() => dividi(n)}>{n}</button>)} rate uguali
        </span>
      </div>
      {rate.map((r, k) => (
        <div key={k} style={{ display:'grid', gridTemplateColumns:'54px 1fr 1fr 34px', gap:'8px', alignItems:'center', marginBottom:'6px' }}>
          <span style={{ fontWeight:700, fontSize:'var(--font-size-sm)' }}>{k + 1}ª rata</span>
          <div>
            <input style={s.inp} type="number" step="0.01" min="0" value={r.importo} onChange={e => set(k, 'importo', e.target.value)} placeholder="Importo €"/>
            {k === 0 && quota > 0 && <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'2px' }}>+ {eur(quota)} tesseramento, da solo</div>}
          </div>
          <div>
            <input style={s.inp} type="date" value={r.scadenza} onChange={e => set(k, 'scadenza', e.target.value)}/>
            <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'2px' }}>da pagare entro</div>
          </div>
          <button type="button" style={{ ...s.icona, color:'#EF4444' }} title="Togli questa rata" disabled={rate.length === 1}
            onClick={() => setEditor(e => ({ ...e, rate: e.rate.filter((_, i) => i !== k) }))}><X size={12}/></button>
        </div>
      ))}
      {rate.length < 12 && (
        <button type="button" style={{ ...s.btnSec, marginBottom:'10px' }} onClick={() => setEditor(e => ({ ...e, rate: [...e.rate, { importo:'', scadenza:'' }] }))}>
          <Plus size={12}/> Aggiungi rata
        </button>
      )}
      <div style={{ fontSize:'var(--font-size-sm)', fontWeight:600, color: torna ? '#16A34A' : '#DC2626', marginBottom:'10px' }}>
        Somma delle rate {eur(somma)} {torna ? '✓ uguale al prezzo' : `— deve essere ${eur(editor.prezzo)} (mancano ${eur(cent(editor.prezzo - somma))})`}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', gap:'8px', flexWrap:'wrap' }}>
        {!editor.nuovo ? <button type="button" style={{ ...s.btnSec, color:'#DC2626' }} onClick={onElimina} disabled={salvando}><Trash2 size={12}/> Togli il piano</button> : <span/>}
        <div style={{ display:'flex', gap:'8px' }}>
          <button type="button" style={s.btnSec} onClick={() => setEditor(null)} disabled={salvando}>Annulla</button>
          <button type="button" style={{ ...s.btnPri, opacity: pronta ? 1 : 0.5 }} disabled={!pronta || salvando}
            onClick={() => onSalva(rate.map(r => ({ importo: r.importo, scadenza: r.scadenza })))}><Check size={13}/> Salva piano</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  info:     { fontSize:'var(--font-size-xs)', color:'var(--text-muted)', background:'var(--bg-input)', borderRadius:'10px', padding:'10px 12px', marginBottom:'10px', lineHeight:1.5 },
  avviso:   { display:'flex', alignItems:'center', gap:'6px', color:'#DC2626', padding:'12px' },
  card:     { background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'12px 14px' },
  piano:    { background:'var(--bg-input)', borderRadius:'10px', padding:'10px 12px', marginTop:'10px' },
  etichetta:{ fontSize:'var(--font-size-xs)', fontWeight:800, letterSpacing:'1px', color:'#C24B08' },
  rata:     { fontSize:'var(--font-size-xs)', background:'var(--bg-card)', border:'1px solid var(--border2)', borderRadius:'99px', padding:'4px 10px' },
  inp:      { background:'var(--bg-card)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'7px 10px', color:'var(--text)', fontSize:'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
  btnPri:   { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'8px 14px', fontSize:'var(--font-size-xs)', fontWeight:700, cursor:'pointer' },
  btnSec:   { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input)', color:'var(--text)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'7px 12px', fontSize:'var(--font-size-xs)', fontWeight:700, cursor:'pointer' },
  icona:    { background:'var(--bg-card)', border:'1px solid var(--border2)', borderRadius:'6px', padding:'5px 7px', color:'var(--text-muted)', cursor:'pointer', display:'inline-flex', alignItems:'center' },
  mini:     { background:'var(--bg-card)', border:'1px solid var(--border2)', borderRadius:'6px', padding:'2px 8px', margin:'0 2px', fontSize:'var(--font-size-xs)', fontWeight:700, cursor:'pointer', color:'var(--text)' },
};
